// lib/frameQueuePolicy.mjs — R114 (Stacy: "photos from past months, many
// closed inspections ago, never upload"). The pure rules the inspection-frame
// queue on the phone (client/src/lib/captureQueue.ts) runs, kept here so
// `node lib/frameQueuePolicy.test.mjs` tests the exact code the phone runs.
//
// The old queue stopped at its first failure, forever (one record whose
// inspection had been wiped 404'd on every sync and blocked everything behind
// it). The rules below never stop the queue: a response either DELETES the
// local copy (only when OUR server confirms it has the photo), RETRIES with a
// backoff (network / timeout / server trouble), or parks the record for a
// person ('attention' — kept on the phone forever, never auto-deleted).

export const DONE = 'done';
export const RETRY = 'retry';
export const ATTENTION = 'attention';

/**
 * What to do with a queued photo after one POST attempt.
 * status 0 = the request never got an answer (offline, timeout, abort).
 * body   = the parsed JSON answer (null when it was not JSON).
 */
export function classifyOutcome(status, body) {
  const s = Number(status) || 0;
  if (s === 0) return RETRY;
  if (s >= 200 && s < 300) {
    // A 2xx only counts when it is OUR server's answer: a hotel captive
    // portal or a proxy page answering 200 with HTML must never delete the
    // only copy of a photo.
    const b = body && typeof body === 'object' ? body : null;
    if (b && (b.placement === 'held' || b.placement === 'attached' || b.deduped === true || Array.isArray(b.urls))) return DONE;
    return RETRY;
  }
  // The server will never take this body as it is (bad request, too large,
  // unsupported type) or the route answered "no such thing" — keep it on the
  // phone for a person to save or discard. Never retried in a loop, never
  // deleted.
  if (s === 400 || s === 404 || s === 410 || s === 413 || s === 415 || s === 422) return ATTENTION;
  // Expired session, a role check that could not run, timeouts, rate limits,
  // and every 5xx (incl. a 503 "store unreadable, retry") — transient.
  if (s === 401 || s === 403 || s === 408 || s === 425 || s === 429 || s >= 500) return RETRY;
  return ATTENTION;
}

/** Backoff after the Nth consecutive transient failure (attempts ≥ 1): 5s, 10s, 20s … capped at 5 min. */
export function backoffMs(attempts) {
  const n = Math.max(1, Math.floor(Number(attempts) || 1));
  return Math.min(5000 * Math.pow(2, Math.min(n - 1, 10)), 300000);
}

/** Per-request upload timeout: 30 s + 15 s per MB sent, capped at 120 s. */
export function uploadTimeoutMs(bytes) {
  const mb = Math.max(0, Number(bytes) || 0) / 1048576;
  return Math.min(120000, Math.round(30000 + 15000 * mb));
}

/**
 * The idempotency key of a record queued by the OLD client (no clientKey was
 * stored). Deterministic from what the row already holds, so the key is the
 * same on every attempt and every session, and no blob row is ever rewritten.
 */
export function legacyKey(id, queuedAt) {
  if (id == null || id === '' || typeof id === 'boolean') return null;
  const n = Number(id);
  if (!Number.isFinite(n) || n < 0) return null;
  const t = Math.max(0, Math.round(Number(queuedAt) || 0));
  return `lg_${Math.floor(n)}_${t.toString(36)}`;
}

/** The key a record is sent with: its own fk_ key, else the derived lg_ key. */
export function keyFor(rec) {
  if (!rec) return null;
  return rec.clientKey || legacyKey(rec.id, rec.queuedAt);
}

const hasId = (v) => v != null && v !== '' && v !== 'null' && v !== 'undefined';
export function inspectionKeyOf(rec) { return rec && hasId(rec.inspectionId) ? String(rec.inspectionId) : null; }

/**
 * Counts for the capture screen. Records whose inspection id is null (queued
 * after Save & exit by the old client) are NOT in the IndexedDB inspectionId
 * index (IDBKeyRange.only(null) throws), so they are counted from the full
 * record list here: unknown = total − every record that has an id.
 *   thisPending   — this inspection's photos still uploading (not rejected)
 *   thisAttention — this inspection's photos the server rejected
 *   olderPending  — everything else on the phone (other inspections + unknown)
 */
export function splitCounts(records, activeId) {
  const recs = Array.isArray(records) ? records : [];
  const act = hasId(activeId) ? String(activeId) : null;
  let indexed = 0, thisAll = 0, thisAttention = 0, attention = 0;
  for (const r of recs) {
    const k = inspectionKeyOf(r);
    if (k != null) indexed++;
    const att = !!(r && r.state === ATTENTION);
    if (att) attention++;
    if (act != null && k === act) { thisAll++; if (att) thisAttention++; }
  }
  const total = recs.length;
  return {
    total,
    unknown: total - indexed,
    attention,
    thisPending: thisAll - thisAttention,
    thisAttention,
    olderPending: total - thisAll,
  };
}

/**
 * The one-line "photos still on this phone" notice — the inspector field view,
 * the office Inspections cockpit and the app-wide pill (an office-role
 * inspector lands on Analytics) all say exactly this. '' when nothing waits.
 */
export function phoneQueueLine(c, uploading) {
  const total = Math.max(0, Math.floor(Number(c && c.total) || 0));
  if (!total) return '';
  const att = Math.max(0, Math.floor(Number(c && c.attention) || 0));
  return `${total} photo${total === 1 ? '' : 's'} on this phone ${uploading ? 'uploading now' : 'waiting to upload'}${att ? ` · ${att} need${att === 1 ? 's' : ''} a look` : ''}`;
}

/**
 * The capture screen's Save & exit tooltip: what is still going on, never a
 * blanket "Everything is saved". Frames are safe on the phone; the walkthrough
 * VIDEO is not (it uploads straight from memory), so while it is uploading the
 * text says to keep the app open.
 */
export function saveExitText(thisPending, videoUploading) {
  const n = Math.max(0, Math.floor(Number(thisPending) || 0));
  const video = videoUploading ? 'the walkthrough video is still uploading — keep the app open until it finishes (it isn’t saved on the phone)' : '';
  if (n) return `${n} photo${n === 1 ? ' is' : 's are'} still uploading — ${n === 1 ? 'it’s' : 'they’re'} saved on this phone and will finish in the background (open the app again if it was closed)${video ? `; ${video}` : ''}`;
  if (video) return `Your photos are saved — ${video}`;
  return 'Everything is saved — pick this inspection back up from the queue any time';
}

// Same window the server's misfile guard uses (lib/inspectionFrames.js):
// a photo taken more than 2 min (clock skew) before the inspection started, or
// more than an hour after it was completed, was probably queued under the
// wrong one.
export const MISFILE_BEFORE_MS = 2 * 60000;
export const MISFILE_AFTER_MS = 60 * 60000;
export function outsideVisit(takenMs, createdAt, completedAt) {
  const t = Number(takenMs);
  if (!Number.isFinite(t) || t <= 0) return false;
  const c = Date.parse(createdAt || '');
  if (Number.isFinite(c) && t < c - MISFILE_BEFORE_MS) return true;
  const d = Date.parse(completedAt || '');
  if (Number.isFinite(d) && t > d + MISFILE_AFTER_MS) return true;
  return false;
}

const dayOf = (ms) => {
  const d = new Date(Number(ms));
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString('en-CA') : '';
};

/** 'YYYY-MM-DD' → "Oct 11" (with the year when it isn't this year). */
export function fmtDay(ymd, now = new Date()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  if (!m) return String(ymd || '');
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  const opts = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  if (+m[1] !== now.getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('en-US', opts);
}

/**
 * One row per inspection for the queue manager sheet, with an HONEST status:
 *   attach    — in progress, will attach
 *   late      — completed, will attach marked "added after completion"
 *   held      — the inspection no longer exists; kept for the office
 *   unknown   — no inspection id at all; kept for the office
 *   rejected  — the server refused every photo in the group (save / discard)
 *   checking  — where they go is not known yet (owners not loaded / offline)
 * owners: { [inspectionId]: { exists, status, listing_name, visit_day, created_at, completed_at } }
 */
export function groupQueue(records, owners) {
  const own = owners && typeof owners === 'object' ? owners : {};
  const groups = new Map();
  for (const r of (Array.isArray(records) ? records : [])) {
    if (!r) continue;
    const k = inspectionKeyOf(r);
    const gk = k == null ? '__none__' : k;
    let g = groups.get(gk);
    if (!g) { g = { key: gk, inspectionId: k, count: 0, bytes: 0, oldest: null, newest: null, attention: 0, misfit: 0, ids: [], legacy: 0 }; groups.set(gk, g); }
    g.count++;
    g.bytes += Number(r.size != null ? r.size : (r.blob && r.blob.size)) || 0;
    const t = Number(r.queuedAt) || 0;
    if (t && (g.oldest == null || t < g.oldest)) g.oldest = t;
    if (t && (g.newest == null || t > g.newest)) g.newest = t;
    if (r.state === ATTENTION) g.attention++;
    if (!r.clientKey) g.legacy++;
    if (r.id != null) g.ids.push(r.id);
    const o = k != null ? own[k] : null;
    if (o && o.exists && !r.clientKey && outsideVisit(t, o.created_at, o.completed_at)) g.misfit++;
  }
  const rows = [];
  for (const g of groups.values()) {
    const o = g.inspectionId != null ? own[g.inspectionId] : null;
    let status, label;
    if (g.attention && g.attention === g.count) {
      status = 'rejected'; label = 'Rejected by the server — save them to the phone, then discard';
    } else if (g.inspectionId == null) {
      status = 'unknown'; label = 'Unknown inspection — will be kept for the office to file';
    } else if (!o) {
      status = 'checking'; label = 'Checking where these go…';
    } else if (!o.exists) {
      status = 'held'; label = 'Inspection no longer exists — will be kept for the office (Recovered photos)';
    } else if (o.status === 'complete') {
      const done = o.completed_at ? fmtDay(dayOf(Date.parse(o.completed_at))) : '';
      status = 'late'; label = `Completed${done ? ` ${done}` : ''} — will attach, marked “added after completion”`;
    } else {
      status = 'attach'; label = 'In progress — will attach';
    }
    if (g.misfit && status !== 'rejected') label += ` · ${g.misfit} taken outside this visit will go to the office to check`;
    if (g.attention && status !== 'rejected') label += ` · ${g.attention} rejected`;
    rows.push({
      ...g,
      status, label,
      title: (o && o.listing_name) || (g.inspectionId == null ? 'Unknown inspection' : 'Unknown property'),
      day: (o && o.visit_day) || (g.oldest ? dayOf(g.oldest) : ''),
    });
  }
  // the office-bound and rejected groups first (they need a decision), then oldest first
  const rank = { rejected: 0, unknown: 1, held: 2, late: 3, checking: 4, attach: 5 };
  rows.sort((a, b) => (rank[a.status] - rank[b.status]) || ((a.oldest || 0) - (b.oldest || 0)));
  return rows;
}

/** Split into batches of n (Save to phone shares ~20 files per tap — each share needs its own tap). */
export function chunk(list, n) {
  const arr = Array.isArray(list) ? list : [];
  const size = Math.max(1, Math.floor(Number(n) || 1));
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/** "2.4 MB" / "640 KB". */
export function fmtBytes(b) {
  const n = Math.max(0, Number(b) || 0);
  if (n >= 1048576) return `${(n / 1048576).toFixed(n >= 10 * 1048576 ? 0 : 1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}
